import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  TooltipContext,
  TooltipContextProps,
} from '../../../../libs/shared/ui/src/components/tooltips/tooltipContext';

/*
 * Constants.
 */

const defaultLines = 2;

/*
 * Props.
 */

interface MultilineTooltipOverflowConditionProps {
  lines?: number;
}

/*
 * Style.
 */

interface StyledMultilineOverflowDivProps {
  $lines: number;
}

const StyledMultilineOverflowDiv = styled.div<StyledMultilineOverflowDivProps>`
  display: -webkit-box;
  -webkit-line-clamp: ${(p) => p.$lines};
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
`;

/*
 * Component.
 */

const MultilineTooltipOverflowConditionComponent: FC<
  TooltipContextProps & MultilineTooltipOverflowConditionProps
> = (props) => {
  const {lines = defaultLines, children, registerCondition} = props;
  const overflowRef = useRef<HTMLDivElement>(null);

  const isOverflowing = () => {
    const currentOverflow = overflowRef.current;
    if (!currentOverflow) {
      return false;
    }

    return currentOverflow.scrollHeight > currentOverflow.offsetHeight;
  };

  useInitialEffect(() => {
    const unregistrator = registerCondition(isOverflowing);

    return () => {
      if (unregistrator) {
        unregistrator();
      }
    };
  });

  return (
    <StyledMultilineOverflowDiv ref={overflowRef} $lines={lines}>
      {children}
    </StyledMultilineOverflowDiv>
  );
};

export const MultilineTooltipOverflowCondition: FC<MultilineTooltipOverflowConditionProps> = ({
  lines,
  children,
}) => (
  <TooltipContext.Consumer>
    {(context) => (
      <MultilineTooltipOverflowConditionComponent lines={lines} registerCondition={context.registerCondition}>
        {children}
      </MultilineTooltipOverflowConditionComponent>
    )}
  </TooltipContext.Consumer>
);
